import { collectBlockAndChildren, getPureBlockLayoutTree } from '@lighthouse/block'
import type {
    AnyObject,
    ApplicationAbstract,
    ApplicationVersionBranch,
    ApplicationVersionStatus,
    ApplicationWebsiteSetting,
    AVPlayUrlResponse,
    BaseApplicationAbstract,
    BlockAbstract,
    BlockType,
    ContainerBlockAbstract,
    DataSourceAbstract,
    DataSourceIdentity,
    IconicProtocol
} from '@lighthouse/core'
import type { FindUseADTObject, FindUseLocation, PureBlockAbstract, VideoCoverUrlResponse } from '@lighthouse/shared'
import { getPureBlockProperty, transformBlockRes2Blocks, transformSyncComponentsRes2Blocks } from '@lighthouse/shared'

import type { TemplateComponent } from '@/atoms/application/type'
import type { HttpResp } from '@/http'
import http from '@/http'

import { JsonContentType } from './constant'
import type {
    AppsByEnvResult,
    BreakPointRes,
    CreateApplicationPayload,
    CreateBreakPointPayload,
    DataSourceRes,
    TemplateComponentsRes,
    UpdateBreakPointPayload
} from './types'
/**
 *
 * 创建应用
 * @param spaceId
 * @param {IconicProtocol} params
 * @return {*}
 */
export async function createApp(params: CreateApplicationPayload): Promise<string> {
    const res = await http.put<unknown, HttpResp<string>>('api/v1/app/createApp', { ...params }, { headers: JsonContentType })
    return res.content
}

/**
 *
 * 验证域名
 * @param spaceId
 * @param {string} domain
 * @return {*}
 */
export async function checkDomain(domain: string): Promise<boolean> {
    const res = await http.get<unknown, HttpResp<boolean>>('api/v1/app/checkDomain', { params: { domain } })
    return res.content
}

/**
 *
 * 复制应用
 * @return {*}
 */
export async function copyApp(params: { targetSpaceId?: string; appId: string }): Promise<string> {
    const res = await http.put<unknown, HttpResp<string>>('api/v1/app/copyApp', { ...params })
    return res.content
}

/**
 *
 * 删除应用
 * @return {*}
 */
export async function deleteApp(appId: string): Promise<boolean> {
    const res = await http.delete<unknown, HttpResp<string>>('api/v1/app/deleteApp', {
        params: { appId }
    })
    return res.success
}

// /**
//  *
//  * 修改应用
//  * @return {*}
//  */
// export async function updateApp(appId: string, params: Partial<IconicProtocol>): Promise<boolean> {
//     const res = await http.post<unknown, HttpResp<string>>(
//         'api/v1/app/updateApp',
//         { appId, ...params },
//         {
//             headers: JsonContentType
//         }
//     )
//     return res.success
// }

/**
 * 获取应用数据
 * @param id
 * @returns
 */
export async function getApp(): Promise<ApplicationAbstract> {
    const resp = await http.get<
        unknown,
        HttpResp<
            Omit<ApplicationAbstract, 'version'> & {
                version: ApplicationVersionBranch & {
                    config: Pick<ApplicationWebsiteSetting, 'authentication' | 'navbar' | 'language' | 'theme'>
                }
            }
        >
    >('api/v1/app/getApp')

    const { version, config, ...rest } = resp.content
    const { config: versionConfig, ...resetVersion } = version

    return {
        ...rest,
        config: { ...config, ...versionConfig },
        version: resetVersion
    }
}

/**
 * 获取应用下的断点列表
 * @return {*}  {Promise<BreakPointRes>}
 */
export async function getBreakPoints(): Promise<BreakPointRes> {
    const res = await http.get<unknown, HttpResp<BreakPointRes>>('api/v1/app/breakPoints')
    return res.content
}

/**
 * 创建断点
 * @return {*}  {Promise<boolean>}
 */
export async function createBreakPoint(payload: CreateBreakPointPayload): Promise<boolean> {
    const res = await http.put<unknown, HttpResp<boolean>>('api/v1/app/createBreakPoint', payload, {
        headers: JsonContentType
    })
    return res.success
}

/**
 * 删除断点
 */
export async function deleteBreakPoint(id: string): Promise<boolean> {
    const res = await http.delete<unknown, HttpResp<boolean>>('api/v1/app/deleteBreakPoint', {
        params: {
            id
        }
    })
    return res.success
}

/**
 * 获取应用下的断点列表
 * @return {*}  {Promise<boolean>}
 */
export async function updateBreakPoint(payload: UpdateBreakPointPayload): Promise<boolean> {
    const res = await http.post<unknown, HttpResp<boolean>>('api/v1/app/updateBreakPoint', payload, {
        headers: JsonContentType
    })
    return res.success
}

type UpdateAppPayload = {
    id: string
    config?: Partial<Pick<ApplicationWebsiteSetting, 'advertising' | 'domain' | 'seo' | 'sms' | 'webApp' | 'webTag' | 'privateTemplate'>>
} & Partial<BaseApplicationAbstract>
/** 更新应用的公共配置 */
export async function updateAppV2(payload: UpdateAppPayload) {
    const res = await http.post<unknown, HttpResp>('api/v1/app/updateApp', payload, { headers: JsonContentType })
    return res.success
}
type UpdateAppVersionConfigPayload = Partial<ApplicationVersionBranch> & {
    id: string
    config?: Partial<Pick<ApplicationWebsiteSetting, 'authentication' | 'language' | 'navbar' | 'theme'>>
}
/** 更新应用版本配置 */
export async function updateAppVersion(payload: UpdateAppVersionConfigPayload) {
    const res = await http.post<unknown, HttpResp>('api/v1/app/updateVersion', payload, { headers: JsonContentType })

    return res.success
}
/** 切换编辑版本预览开关 */
export async function switchAppVersionPreview(payload: boolean) {
    const res = await http.post<unknown, HttpResp>('api/v1/app/switchPreview', { preview: payload })
    return res.success
}

/**
 * 上下线应用
 * @return {*}  {Promise<boolean>}
 */
export async function updateAppStatus(status: Extract<ApplicationVersionStatus, 'ONLINE' | 'OFFLINE'>) {
    const res = await http.post<unknown, HttpResp>('api/v1/app/updateStatus', { status })
    return res.success
}

/**
 * @description 获取自定义域名的绑定状态
 * @param {string} domain
 * @return {*}  {Promise<boolean>}
 */
export async function getDomainBindingStatus(params: { domain: string; abort?: AbortController }): Promise<boolean | undefined> {
    const { abort, domain } = params
    const res = await http.get<unknown, HttpResp<boolean> | undefined>('api/v1/app/getDomainBindingStatus', {
        params: {
            domain
        },
        signal: abort?.signal
    })
    return res?.content
}

/**
 * @description 创建模板应用
 * @param {string} templateId
 * @return {*}  {Promise<string | undefined>}
 */
export async function createAppTemplate(params: { templateId: string; spaceId: string }): Promise<string> {
    const res = await http.put<unknown, HttpResp<string>>('api/v1/app/createAppByTemplate', params)
    return res.content
}

/**
 * 获取应用下数据源列表
 * @return {*}
 */
export async function listDs(appId: string, envId: string): Promise<DataSourceAbstract[]> {
    const res = await http.get<unknown, HttpResp<DataSourceRes[]>>('/api/v1/app/listDs', { params: { appId, envId } })
    return res.content.map(item => ({
        appId,
        envId,
        ...item
    }))
}

/**
 *
 * @description 发送邮件分享应用
 */
export async function sendEmailSharing(params: { mails: string[]; buttonUrl: string }): Promise<boolean> {
    const res = await http.put<unknown, HttpResp>('api/v1/app/emailSharing', params, {
        headers: JsonContentType
    })
    return res.success
}

/**
 * 获取阿里云视频播放凭证
 */
export async function getVideoPlayAuth(videoId: string) {
    const res = await http.get<unknown, HttpResp<{ playAuth: string }>>('api/v1/oss/getVideoPlayAuth', {
        params: {
            videoId
        }
    })
    return res.content?.playAuth
}

/**
 * 签发视频播放地址（HLS + CDN 鉴权）
 */
export async function fetchAVPlayUrl(originalUrl: string, isOrigin?: boolean) {
    const res = await http.post<unknown, HttpResp<AVPlayUrlResponse>>('api/v1/oss/video/play-url', undefined, {
        params: { originalUrl, isOrigin }
    })
    return res?.content ?? null
}

/**
 * 获取视频上传时解析的首帧封面地址
 */
export async function fetchVideoCoverUrl(fileId: string) {
    const res = await http.post<unknown, HttpResp<VideoCoverUrlResponse>>('api/v1/oss/video/cover-url', undefined, { params: { fileId } })
    return res?.content ?? null
}

/**
 * 获取数据源excel模板
 */
export async function downloadImportTemplate(payload: DataSourceIdentity & { dsName: string }) {
    const { dsName, ...rest } = payload
    // /api/v1/oss/downloadImportTemplate
    const response = await http.get('/api/auth/v1/downloadImportTemplate', {
        params: rest,
        responseType: 'blob'
    })
    const blob = response.data as Blob

    // 创建一个隐藏的<a>元素，并设置其属性
    const downloadLink = document.createElement('a')
    const objectUrl = window.URL.createObjectURL(blob)
    downloadLink.href = objectUrl
    downloadLink.download = `${dsName}.xlsx`
    downloadLink.style.display = 'none'
    document.body.appendChild(downloadLink)

    // 触发点击事件以下载文件
    downloadLink.click()
    downloadLink.remove()

    // 释放URL对象
    window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl)
    }, 0)
    return true
}

/**
 * 查找引用
 * @return {*}  {Promise<boolean>}
 */
export async function getFindUses(payload: FindUseADTObject): Promise<FindUseLocation> {
    const res = await http.post<unknown, HttpResp<FindUseLocation>>('api/v1/app/findUses', { ...payload }, { headers: JsonContentType })
    return res.content
}

/**
 * 获取应用版本列表
 */
export async function getAppVersionList() {
    const res = await http.get<unknown, HttpResp<ApplicationVersionBranch[]>>('api/v1/app/versions')
    return res.content
}
/**
 * 创建应用版本分支
 */
export async function createAppVersion(payload: Pick<ApplicationVersionBranch, 'name' | 'remark'>) {
    const res = await http.put<unknown, HttpResp<string>>('api/v1/app/createVersion', payload)
    return res.content
}
/**
 * 切换应用版本分支
 */
export async function switchAppVersion(versionId: string) {
    const res = await http.post<unknown, HttpResp<string>>('api/v1/app/switchVersion', { versionId })
    return res.success
}
/**
 * 合并应用版本分支
 */
export async function mergeAppVersion() {
    const res = await http.post<unknown, HttpResp<number>>('api/v1/app/merge')
    return res.content
}
/**
 * 删除应用版本分支
 */
export async function deleteAppVersion(versionId: string) {
    const res = await http.delete<unknown, HttpResp<boolean>>('api/v1/app/deleteVersion', { params: { versionId } })
    return res.success
}
/**
 * 获取版本是否有更新
 */
export async function checkVersionUpdated() {
    const res = await http.get<unknown, HttpResp<boolean>>('api/v1/app/hasUpdates')
    return res.content
}

/** 获取使用指定环境的应用
 * @return {*}
 */
export async function getAppsByEnv(envId: string): Promise<AppsByEnvResult> {
    const res = await http.get<unknown, HttpResp<AppsByEnvResult>>('api/v1/app/getAppsByEnv', {
        params: { envId }
    })
    return res.content
}

/** 获取验证码
 * @return {*}
 */
export async function getVerifyCode(params: { mobile: string; blockId: string; pageId: string }): Promise<boolean> {
    const res = await http.get<unknown, HttpResp<boolean>>('api/auth/v1/sms/getFormVerifyCode', {
        params
    })
    return res.success
}

/** 获取同步组件 */
export async function getSyncComponents() {
    const res = await http.get<
        unknown,
        HttpResp<
            (Omit<BlockAbstract, 'config'> & {
                config: Record<BlockType, AnyObject>
            })[]
        >
    >('api/v1/app/getSyncBlocks')

    return res.content ? (transformSyncComponentsRes2Blocks(res.content) as ContainerBlockAbstract[]) : []
}
/** 更新同步组件 */
export async function updateSyncComponents(payload: BlockAbstract[]) {
    const pureBlockProperties = payload.reduce<PureBlockAbstract[]>(
        (total, curr) => [...total, ...collectBlockAndChildren(curr).map(getPureBlockProperty)],
        []
    )
    const pureBlockLayouts = payload.map(getPureBlockLayoutTree)

    const res = await http.post<unknown, HttpResp<boolean>>(
        'api/v1/app/updateSyncBlocks',
        {
            nodes: pureBlockLayouts,
            blocks: pureBlockProperties.map(item => ({
                ...item,
                config: {
                    [item.type]: item.config
                }
            }))
        },
        { headers: JsonContentType }
    )
    return res.success
}

/** 创建一个同步组件 */
export async function createSyncBlock({ createBlocks, blocks }: { createBlocks: BlockAbstract[]; blocks: BlockAbstract[] }) {
    const pureBlockProperties = createBlocks.reduce<PureBlockAbstract[]>(
        (total, curr) => [...total, ...collectBlockAndChildren(curr).map(getPureBlockProperty)],
        []
    )
    const pureBlockLayouts = blocks.map(getPureBlockLayoutTree)

    const res = await http.post<unknown, HttpResp<boolean>>(
        `api/v1/app/createSyncBlocks`,
        {
            nodes: pureBlockLayouts,
            blocks: pureBlockProperties.map(item => ({
                ...item,
                config: {
                    [item.type]: item.config
                }
            }))
        },
        { headers: JsonContentType }
    )

    return res.success
}

/** 删除同步组件 */
export async function deleteSyncBlock(id: string) {
    const res = await http.delete<unknown, HttpResp<boolean>>(`api/v1/app/deleteSyncBlocks`, {
        params: { blockId: id }
    })

    return res.success
}

/** 删除同步组件子节点 */
export async function deleteSyncLeafBlocks({
    removedBlocks,
    blocks,
    pageId
}: {
    removedBlocks: BlockAbstract[]
    blocks: BlockAbstract[]
    pageId: string
}) {
    const ids = removedBlocks.reduce<string[]>((total, curr) => [...total, ...collectBlockAndChildren(curr).map(item => item.id)], [])
    const res = await http.delete<unknown, HttpResp<boolean>>(`api/v1/app/deleteSyncLeafBlocks`, {
        data: { blockIds: ids, nodes: blocks.map(getPureBlockLayoutTree), pageId },
        headers: JsonContentType
    })

    return res.success
}

/** 获取模板组件库
 * @return {TemplateComponentsRes}
 */
export async function getTemplateComponents() {
    const res = await http.get<unknown, HttpResp<TemplateComponentsRes>>(`api/v1/app/templateComponents`)
    const list: TemplateComponent[] = res.content?.map(item => {
        const { components, ...rest } = item
        const componentList = components.map(component => {
            const { block } = component
            const transformBlocks = transformBlockRes2Blocks([block])
            return {
                ...component,
                block: transformBlocks[0]
            }
        })
        return {
            ...rest,
            components: componentList
        }
    })
    return list
}

/**
 * 更新所有页面
 * @param {UpdatePagePayload} data
 */
export async function updatePageNavbar(data: { isShowNavbar?: boolean; isShowTabbar?: boolean }): Promise<boolean> {
    const res = await http.post<unknown, HttpResp>('api/v1/app/updatePageNavbar', data, {
        // headers: JsonContentType
    })
    return res.success
}
